// Updated validation constants with enhanced password validation
const VALIDATION_RULES = {
  EMAIL: {
    REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    MESSAGES: {
      REQUIRED: "Email is required",
      INVALID: "Please enter a valid email address",
    },
  },
  USERNAME: {
    MIN_LENGTH: 3,
    MAX_LENGTH: 30,
    REGEX: /^[a-zA-Z0-9_]+$/,
    NUMERIC_UNDERSCORE_ONLY: /^[0-9_]+$/,
    START_END_UNDERSCORE: /^_|_$/,
    MESSAGES: {
      REQUIRED: "Username is required",
      MIN_LENGTH: "Username must be at least 3 characters long",
      MAX_LENGTH: "Username must be no more than 30 characters long",
      INVALID_CHARS:
        "Username can only contain letters, numbers, and underscores",
      MUST_CONTAIN_LETTER: "Username must contain at least one letter",
      NO_START_END_UNDERSCORE:
        "Username cannot start or end with an underscore",
    },
  },
  FULL_NAME: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 100,
    REGEX: /^[a-zA-Z\s\-'.]+$/,
    MESSAGES: {
      REQUIRED: "Full name is required",
      MIN_LENGTH: "Full name must be at least 2 characters long",
      MAX_LENGTH: "Full name must be no more than 100 characters long",
      INVALID_CHARS:
        "Full name can only contain letters, spaces, hyphens, apostrophes, and periods",
    },
  },
  PASSWORD: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 128,
    // Enhanced password patterns
    HAS_UPPERCASE: /[A-Z]/,
    HAS_NUMBER: /[0-9]/,
    HAS_SYMBOL: /[!@#$%^&*()_+\-=[\]{}|;':"\\<>,.?/~`]/,
    SAME_CHAR_REGEX: /^(.)\1*$/,
    MESSAGES: {
      REQUIRED: "Password is required",
      MIN_LENGTH: "Password must be at least 8 characters long",
      MAX_LENGTH: "Password must be no more than 128 characters long",
      UPPERCASE: "Password must contain at least one uppercase letter",
      NUMBER: "Password must contain at least one number",
      SYMBOL: "Password must contain at least one special symbol ",
      SAME_CHAR: "Password cannot be all the same character",
      CONFIRM_REQUIRED: "Please confirm your password",
      MISMATCH: "Passwords do not match",
    },
  },
  USER_TYPE: {
    MESSAGES: {
      INVALID: "Invalid user type selection",
    },
  },
  PATRON_TYPE: {
    VALID_TYPES: ["student", "faculty", "visitor", "researcher"],
    MESSAGES: {
      REQUIRED: "Patron type is required when selecting Patron role",
      INVALID: "Please select a valid patron type",
    },
  },
};

const isEmptyOrWhitespace = (value) => !value?.trim();

const validateEmail = (email) => {
  if (isEmptyOrWhitespace(email)) {
    return VALIDATION_RULES.EMAIL.MESSAGES.REQUIRED;
  }

  if (!VALIDATION_RULES.EMAIL.REGEX.test(email.trim())) {
    return VALIDATION_RULES.EMAIL.MESSAGES.INVALID;
  }

  return null;
};

const validateUsername = (username) => {
  if (isEmptyOrWhitespace(username)) {
    return VALIDATION_RULES.USERNAME.MESSAGES.REQUIRED;
  }

  const trimmedUsername = username.trim();
  const {
    MIN_LENGTH,
    MAX_LENGTH,
    REGEX,
    NUMERIC_UNDERSCORE_ONLY,
    START_END_UNDERSCORE,
    MESSAGES,
  } = VALIDATION_RULES.USERNAME;

  if (trimmedUsername.length < MIN_LENGTH) {
    return MESSAGES.MIN_LENGTH;
  }

  if (trimmedUsername.length > MAX_LENGTH) {
    return MESSAGES.MAX_LENGTH;
  }

  if (!REGEX.test(trimmedUsername)) {
    return MESSAGES.INVALID_CHARS;
  }

  if (NUMERIC_UNDERSCORE_ONLY.test(trimmedUsername)) {
    return MESSAGES.MUST_CONTAIN_LETTER;
  }

  if (START_END_UNDERSCORE.test(trimmedUsername)) {
    return MESSAGES.NO_START_END_UNDERSCORE;
  }

  return null;
};

const validateFullName = (fullName) => {
  if (isEmptyOrWhitespace(fullName)) {
    return VALIDATION_RULES.FULL_NAME.MESSAGES.REQUIRED;
  }

  const trimmedName = fullName.trim();
  const { MIN_LENGTH, MAX_LENGTH, REGEX, MESSAGES } =
    VALIDATION_RULES.FULL_NAME;

  if (trimmedName.length < MIN_LENGTH) {
    return MESSAGES.MIN_LENGTH;
  }

  if (trimmedName.length > MAX_LENGTH) {
    return MESSAGES.MAX_LENGTH;
  }

  if (!REGEX.test(trimmedName)) {
    return MESSAGES.INVALID_CHARS;
  }

  return null;
};

const validatePassword = (password) => {
  if (!password) {
    return VALIDATION_RULES.PASSWORD.MESSAGES.REQUIRED;
  }

  const {
    MIN_LENGTH,
    MAX_LENGTH,
    HAS_UPPERCASE,
    HAS_NUMBER,
    HAS_SYMBOL,
    SAME_CHAR_REGEX,
    MESSAGES,
  } = VALIDATION_RULES.PASSWORD;

  if (password.length < MIN_LENGTH) {
    return MESSAGES.MIN_LENGTH;
  }

  if (password.length > MAX_LENGTH) {
    return MESSAGES.MAX_LENGTH;
  }

  if (!HAS_UPPERCASE.test(password)) {
    return MESSAGES.UPPERCASE;
  }

  if (!HAS_NUMBER.test(password)) {
    return MESSAGES.NUMBER;
  }

  if (!HAS_SYMBOL.test(password)) {
    return MESSAGES.SYMBOL;
  }

  if (SAME_CHAR_REGEX.test(password)) {
    return MESSAGES.SAME_CHAR;
  }

  return null;
};

const validatePasswordConfirm = (password, passwordConfirm) => {
  if (!passwordConfirm) {
    return VALIDATION_RULES.PASSWORD.MESSAGES.CONFIRM_REQUIRED;
  }

  if (password !== passwordConfirm) {
    return VALIDATION_RULES.PASSWORD.MESSAGES.MISMATCH;
  }

  return null;
};

const validatePatronType = (values) => {
  const memberTypes = values.member_types;
  const patronType = values.patron_type;

  // Check if patron is selected in member_types
  const isPatronSelected =
    memberTypes && Array.isArray(memberTypes) && memberTypes.includes("patron");

  // If patron is selected, patron_type is required
  if (isPatronSelected) {
    if (!patronType || isEmptyOrWhitespace(patronType)) {
      return VALIDATION_RULES.PATRON_TYPE.MESSAGES.REQUIRED;
    }

    // Validate that the patron_type is one of the valid options
    if (!VALIDATION_RULES.PATRON_TYPE.VALID_TYPES.includes(patronType.trim())) {
      return VALIDATION_RULES.PATRON_TYPE.MESSAGES.INVALID;
    }
  }

  return null;
};

const validateUserType = (values) => {
  const memberTypes = values.member_types;

  // No longer require selection - empty is allowed (default user)
  if (memberTypes && Array.isArray(memberTypes)) {
    // Updated to include 'patron' as valid type
    const validTypes = ["patron", "librarian"];
    const invalidTypes = memberTypes.filter(
      (type) => !validTypes.includes(type)
    );

    if (invalidTypes.length > 0) {
      return VALIDATION_RULES.USER_TYPE.MESSAGES.INVALID;
    }
  }

  return null;
};

// Main validation function
export const validateForm = (values) => {
  const errors = {};

  // Run all validations
  const emailError = validateEmail(values.email);
  if (emailError) errors.email = emailError;

  const usernameError = validateUsername(values.username);
  if (usernameError) errors.username = usernameError;

  const fullNameError = validateFullName(values.full_name);
  if (fullNameError) errors.full_name = fullNameError;

  const passwordError = validatePassword(values.password);
  if (passwordError) errors.password = passwordError;

  const passwordConfirmError = validatePasswordConfirm(
    values.password,
    values.password_confirm
  );
  if (passwordConfirmError) errors.password_confirm = passwordConfirmError;

  // Validate member type selection (now optional)
  const memberTypeError = validateUserType(values);
  if (memberTypeError) errors.member_types = memberTypeError;

  // Validate patron type (required when patron is selected)
  const patronTypeError = validatePatronType(values);
  if (patronTypeError) errors.patron_type = patronTypeError;

  return errors;
};

// Field-specific validation functions for real-time validation
export const validateEmailField = (email) => validateEmail(email);
export const validateUsernameField = (username) => validateUsername(username);
export const validateFullNameField = (fullName) => validateFullName(fullName);
export const validatePasswordField = (password) => validatePassword(password);
export const validatePasswordConfirmField = (password, passwordConfirm) =>
  validatePasswordConfirm(password, passwordConfirm);
export const validatePatronTypeField = (values) => validatePatronType(values);

const EXCLUDED_FIELDS = ["password_confirm", "_submitButton"];

export const prepareSubmitData = (values) => {
  const submitData = Object.keys(values)
    .filter((key) => !EXCLUDED_FIELDS.includes(key))
    .reduce((acc, key) => {
      acc[key] = values[key];
      return acc;
    }, {});

  // Trim and normalize text fields
  if (submitData.email) {
    submitData.email = submitData.email.trim().toLowerCase();
  }

  if (submitData.username) {
    submitData.username = submitData.username.trim();
  }

  if (submitData.full_name) {
    submitData.full_name = submitData.full_name.trim();
  }

  // Handle patron_type - trim if present
  const patronTypeField = "patron_type";
  if (submitData[patronTypeField]) {
    submitData[patronTypeField] = submitData[patronTypeField].trim();
  }

  // Handle member_types - if empty, send as empty array or don't send at all
  const memberTypesField = "member_types";
  if (
    !submitData[memberTypesField] ||
    submitData[memberTypesField].length === 0
  ) {
    submitData[memberTypesField] = []; // Backend will treat empty array as default user
    // Clear patron_type if no member types selected
    delete submitData[patronTypeField];
  } else {
    // If patron is not selected, remove patron_type from submission
    const isPatronSelected = submitData[memberTypesField].includes("patron");
    if (!isPatronSelected) {
      delete submitData[patronTypeField];
    }
  }

  // Process user type data based on structure (if using object format)
  if (submitData.user_type && typeof submitData.user_type === "object") {
    // Convert boolean object to array of selected types
    const selectedTypes = [];
    if (submitData.user_type.patron) selectedTypes.push("patron");
    if (submitData.user_type.librarian) selectedTypes.push("librarian");

    submitData[memberTypesField] = selectedTypes;
    delete submitData.user_type; // Clean up
  }

  // Handle direct boolean properties (if that's how your form sends them)
  if (submitData.patron || submitData.librarian) {
    const selectedTypes = [];
    if (submitData.patron) selectedTypes.push("patron");
    if (submitData.librarian) selectedTypes.push("librarian");

    submitData[memberTypesField] = selectedTypes;

    // Clean up individual boolean fields
    delete submitData.patron;
    delete submitData.librarian;
  }

  return submitData;
};

// Utility function to check if patron type is required based on current form state
export const isPatronTypeRequired = (memberTypes) => {
  return (
    memberTypes && Array.isArray(memberTypes) && memberTypes.includes("patron")
  );
};

// Helper function to get password strength for UI feedback
export const getPasswordStrength = (password) => {
  if (!password) return { score: 0, feedback: [] };

  const feedback = [];
  let score = 0;

  const { MIN_LENGTH, HAS_UPPERCASE, HAS_NUMBER, HAS_SYMBOL, SAME_CHAR_REGEX } =
    VALIDATION_RULES.PASSWORD;

  // Length check
  if (password.length >= MIN_LENGTH) {
    score += 1;
  } else {
    feedback.push(`At least ${MIN_LENGTH} characters`);
  }

  // Uppercase check
  if (HAS_UPPERCASE.test(password)) {
    score += 1;
  } else {
    feedback.push("One uppercase letter");
  }

  // Number check
  if (HAS_NUMBER.test(password)) {
    score += 1;
  } else {
    feedback.push("One number");
  }

  // Symbol check
  if (HAS_SYMBOL.test(password)) {
    score += 1;
  } else {
    feedback.push("One special symbol");
  }

  // Same character check
  if (SAME_CHAR_REGEX.test(password)) {
    score = 0;
    feedback.push("Cannot be all the same character");
  }

  return { score, feedback };
};

// Utility function for async validation (if needed for username/email uniqueness checks)
export const validateFormAsync = async (values, asyncValidators = {}) => {
  const errors = validateForm(values);

  // Run async validations if provided
  if (asyncValidators.email && values.email && !errors.email) {
    const emailError = await asyncValidators.email(values.email);
    if (emailError) errors.email = emailError;
  }

  if (asyncValidators.username && values.username && !errors.username) {
    const usernameError = await asyncValidators.username(values.username);
    if (usernameError) errors.username = usernameError;
  }

  return errors;
};
